import type { LoggerService } from '../LoggerService'
import { sanitizeUrl } from '../sanitize'

type LayoutShiftEntry = PerformanceEntry & {
  value: number
  hadRecentInput: boolean
}

type ScriptTiming = {
  duration: number
  executionStart?: number
  forcedStyleAndLayoutDuration?: number
  invoker?: string
  invokerType?: string
  pauseDuration?: number
  sourceCharPosition?: number
  sourceURL?: string
  sourceFunctionName?: string
  windowAttribution?: string
}

type LongAnimationFrameEntry = PerformanceEntry & {
  blockingDuration?: number
  firstUIEventTimestamp?: number
  renderStart?: number
  styleAndLayoutStart?: number
  scripts?: ScriptTiming[]
}

type TaskAttribution = {
  name?: string
  containerType?: string
  containerName?: string
  containerId?: string
  containerSrc?: string
}

type LongTaskEntry = PerformanceEntry & {
  attribution?: TaskAttribution[]
}

const LONG_WORK_THRESHOLD_MS = 200
const MAX_LONG_WORK_REPORTS = 10
const MAX_SCRIPT_ATTRIBUTIONS = 5
const MAX_SLOW_RESOURCES = 5

type VitalRating = 'good' | 'needs-improvement' | 'poor'

function rateVital(
  value: number | undefined,
  goodThreshold: number,
  poorThreshold: number
): VitalRating | undefined {
  if (value == null) return undefined
  if (value <= goodThreshold) return 'good'
  if (value <= poorThreshold) return 'needs-improvement'
  return 'poor'
}

function overallCoreWebVitalsRating(
  ratings: Array<VitalRating | undefined>
): VitalRating | 'incomplete' {
  if (ratings.some(rating => rating == null)) return 'incomplete'
  if (ratings.includes('poor')) return 'poor'
  if (ratings.includes('needs-improvement')) return 'needs-improvement'
  return 'good'
}

function optionalUrl(value: string | undefined): string | undefined {
  return value ? sanitizeUrl(value) : undefined
}

function longAnimationFramePayload(entry: LongAnimationFrameEntry) {
  const scripts = [...(entry.scripts ?? [])]
    .sort((a, b) => b.duration - a.duration)
    .slice(0, MAX_SCRIPT_ATTRIBUTIONS)
    .map(script => ({
      durationMs: Math.round(script.duration),
      executionStartMs: script.executionStart
        ? Math.round(script.executionStart)
        : undefined,
      pauseDurationMs: Math.round(script.pauseDuration ?? 0),
      forcedStyleAndLayoutMs: Math.round(
        script.forcedStyleAndLayoutDuration ?? 0
      ),
      sourceUrl: optionalUrl(script.sourceURL),
      functionName: script.sourceFunctionName || undefined,
      sourceCharPosition: script.sourceCharPosition,
      invoker: script.invoker || undefined,
      invokerType: script.invokerType || undefined,
      windowAttribution: script.windowAttribution || undefined
    }))

  return {
    durationMs: Math.round(entry.duration),
    blockingDurationMs: Math.round(entry.blockingDuration ?? 0),
    frameStartMs: Math.round(entry.startTime),
    renderStartMs: entry.renderStart
      ? Math.round(entry.renderStart)
      : undefined,
    styleAndLayoutStartMs: entry.styleAndLayoutStart
      ? Math.round(entry.styleAndLayoutStart)
      : undefined,
    triggeredByUiEvent: (entry.firstUIEventTimestamp ?? 0) > 0,
    attributionAvailable: scripts.length > 0,
    scripts
  }
}

function longTaskPayload(entry: LongTaskEntry) {
  const attribution = (entry.attribution ?? []).map(item => ({
    taskName: item.name,
    containerType: item.containerType,
    containerName: item.containerName,
    containerId: item.containerId,
    containerSrc: optionalUrl(item.containerSrc)
  }))

  return {
    durationMs: Math.round(entry.duration),
    taskStartMs: Math.round(entry.startTime),
    taskName: entry.name,
    attributionAvailable: attribution.length > 0,
    attribution,
    attributionLimitation:
      attribution.length === 0
        ? 'long_tasks_api_does_not_expose_javascript_stacks'
        : undefined
  }
}

export function mountPerformanceInterceptor(logger: LoggerService): () => void {
  if (typeof PerformanceObserver === 'undefined') return () => undefined

  const observers: PerformanceObserver[] = []
  let lcpMs: number | undefined
  let cls = 0
  let inpMs: number | undefined
  let reported = false
  let longWorkReports = 0

  const observe = (
    type: string,
    callback: (entries: PerformanceEntry[]) => void,
    options?: PerformanceObserverInit
  ) => {
    try {
      const observer = new PerformanceObserver(list =>
        callback(list.getEntries())
      )
      observer.observe(options ?? { type, buffered: true })
      observers.push(observer)
    } catch {
      // Unsupported entry types are expected across browsers.
    }
  }

  observe('largest-contentful-paint', entries => {
    const last = entries.at(-1)
    if (last) lcpMs = Math.round(last.startTime)
  })
  observe('layout-shift', entries => {
    entries.forEach(raw => {
      const entry = raw as LayoutShiftEntry
      if (!entry.hadRecentInput) cls += entry.value
    })
  })
  observe(
    'event',
    entries => {
      entries.forEach(entry => {
        inpMs = Math.max(inpMs ?? 0, Math.round(entry.duration))
      })
    },
    {
      type: 'event',
      buffered: true,
      durationThreshold: 40
    } as PerformanceObserverInit
  )
  const supportsLongAnimationFrames =
    PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame')

  if (supportsLongAnimationFrames) {
    observe('long-animation-frame', entries => {
      entries.forEach(raw => {
        if (
          raw.duration < LONG_WORK_THRESHOLD_MS ||
          longWorkReports >= MAX_LONG_WORK_REPORTS
        )
          return
        longWorkReports += 1
        logger.warn(
          'performance.long_animation_frame',
          longAnimationFramePayload(raw as LongAnimationFrameEntry)
        )
      })
    })
  } else {
    observe('longtask', entries => {
      entries.forEach(raw => {
        if (
          raw.duration < LONG_WORK_THRESHOLD_MS ||
          longWorkReports >= MAX_LONG_WORK_REPORTS
        )
          return
        longWorkReports += 1
        logger.warn(
          'performance.long_task',
          longTaskPayload(raw as LongTaskEntry)
        )
      })
    })
  }

  const report = () => {
    if (reported) return
    reported = true
    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined
    const paints = performance.getEntriesByType('paint')
    const fcpMs = paints.find(
      entry => entry.name === 'first-contentful-paint'
    )?.startTime
    const resources = performance.getEntriesByType(
      'resource'
    ) as PerformanceResourceTiming[]
    const slowestResources = [...resources]
      .sort((a, b) => b.duration - a.duration)
      .slice(0, MAX_SLOW_RESOURCES)
      .map(resource => ({
        url: optionalUrl(resource.name),
        initiatorType: resource.initiatorType,
        durationMs: Math.round(resource.duration),
        transferSizeBytes: resource.transferSize
      }))
    const roundedLcp = lcpMs
    const roundedInp = inpMs
    const roundedFcp = fcpMs == null ? undefined : Math.round(fcpMs)
    const ttfbMs = navigation ? Math.round(navigation.responseStart) : undefined
    const lcpRating = rateVital(roundedLcp, 2_500, 4_000)
    const clsRating = rateVital(cls, 0.1, 0.25)
    const inpRating = rateVital(roundedInp, 200, 500)
    const missingCoreVitals = [
      roundedLcp == null ? 'lcp' : undefined,
      roundedInp == null ? 'inp' : undefined
    ].filter((name): name is string => !!name)
    logger.log(
      'performance.web_vitals',
      {
        lcpMs: roundedLcp,
        lcpRating,
        cls: Number(cls.toFixed(4)),
        clsRating,
        inpMs: roundedInp,
        inpRating,
        fcpMs: roundedFcp,
        fcpRating: rateVital(roundedFcp, 1_800, 3_000),
        ttfbMs,
        ttfbRating: rateVital(ttfbMs, 800, 1_800),
        overallCoreWebVitalsRating: overallCoreWebVitalsRating([
          lcpRating,
          clsRating,
          inpRating
        ]),
        missingCoreVitals,
        collectionState: missingCoreVitals.length > 0 ? 'partial' : 'complete',
        navigationType: navigation?.type,
        redirectCount: navigation?.redirectCount,
        domInteractiveMs: navigation
          ? Math.round(navigation.domInteractive)
          : undefined,
        domContentLoadedMs: navigation
          ? Math.round(navigation.domContentLoadedEventEnd)
          : undefined,
        loadMs: navigation ? Math.round(navigation.loadEventEnd) : undefined,
        postDomContentLoadedDelayMs:
          navigation?.loadEventEnd && navigation.domContentLoadedEventEnd
            ? Math.max(
                0,
                Math.round(
                  navigation.loadEventEnd - navigation.domContentLoadedEventEnd
                )
              )
            : undefined,
        navigationTransferSizeBytes: navigation?.transferSize,
        resourceCount: resources.length,
        slowResourceCount: resources.filter(
          resource => resource.duration >= 1_000
        ).length,
        totalResourceTransferSizeBytes: resources.reduce(
          (total, resource) => total + resource.transferSize,
          0
        ),
        slowestResources,
        longWorkReports,
        documentReadyState: document.readyState
      },
      { category: 'performance', level: 'info' }
    )
  }
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') report()
  }
  document.addEventListener('visibilitychange', onVisibility)

  return () => {
    report()
    observers.forEach(observer => observer.disconnect())
    document.removeEventListener('visibilitychange', onVisibility)
  }
}
